import { DownloadSimpleIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useExportUnits } from '@/api/units/export-units'
import { GetUnitsRequest, useGetUnits } from '@/api/units/get-units'
import Dashboard from '@/components/Dashboard'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Icon } from '@/components/ui/icon'
import { Select } from '@/components/ui/select'
import { StatusTabOption, StatusTabs } from '@/components/ui/status-tabs'
import { RowAction, SortOrder } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { routerFactory } from '@/factories'
import { useFilters, useSearchParams } from '@/hooks'
import { DateRange } from '@/models'
import { LeaseStatus, UnitListItem, UnitOccupancyStatus } from '@/models/features'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { buildCsvFilename } from '@/utils/file'

import {
  getLeaseStatusOptions,
  getUnitsColumns,
  UNITS_TABLE_ID,
  unitsInitialFilters,
  unitsQueryFiltersSchema,
} from './units.page.constants'
import { UnitOccupancyTab, UnitOccupancyTabEnum } from './units.page.types'

export const UnitsPage = () => {
  const { t } = useTranslation(['units'])
  const activeBuilding = useSelector(selectActiveBuilding)
  const { navigate } = routerFactory.useRouter()

  const searchParams = useSearchParams({
    initialState: unitsInitialFilters,
    schema: unitsQueryFiltersSchema,
  })

  const filters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: unitsInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const filtersState = filters.filters

  const queryParams: GetUnitsRequest = {
    buildingId: activeBuilding?.id,
    dateFrom: filtersState.fromDate,
    dateTo: filtersState.toDate,
    leaseStatus: filtersState.leaseStatus,
    search: filtersState.search?.trim() || undefined,
    sortBy: filtersState.sortBy,
    sortOrder: filtersState.sortOrder,
    unitStatus:
      filtersState.status && filtersState.status !== UnitOccupancyTabEnum.enum.all
        ? (filtersState.status.toLowerCase() as Lowercase<UnitOccupancyStatus>)
        : 'all',
  }

  const {
    data,
    isFetching: isUnitsFetching,
    error: unitsError,
  } = useGetUnits({
    ...queryParams,
    limit: filtersState.limit,
    skip: filtersState.skip,
  })
  const { total = 0, units = [], occupancyCounts } = data || {}

  const { mutate: exportUnits, isPending: isExportPending } = useExportUnits()

  const statusTabsOptions: StatusTabOption<UnitOccupancyTab>[] = [
    {
      count: occupancyCounts ? occupancyCounts.occupied + occupancyCounts.vacant : undefined,
      label: t('units:list.tabs.all'),
      value: UnitOccupancyTabEnum.enum.all,
    },
    {
      count: occupancyCounts?.occupied,
      label: t('units:list.tabs.occupied'),
      value: UnitOccupancyTabEnum.enum.Occupied,
    },
    {
      count: occupancyCounts?.vacant,
      label: t('units:list.tabs.vacant'),
      value: UnitOccupancyTabEnum.enum.Vacant,
    },
  ]

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    filters.setFilter('search', e.target.value)
  }

  const handleStatusChange = (status: UnitOccupancyTab) => {
    filters.setFilter('status', status)
  }

  const handleDateChange = (range: DateRange) => {
    filters.setFilters({ fromDate: range[0]?.getTime(), toDate: range[1]?.getTime() })
  }

  const handleLeaseStatusChange = (leaseStatus: LeaseStatus[]) => {
    filters.setFilter('leaseStatus', leaseStatus.length ? leaseStatus : undefined)
  }

  const handleSort = (sortBy: string | null, sortOrder: SortOrder | null) => {
    filters.setFilters({
      sortBy: sortBy ?? undefined,
      sortOrder: sortOrder ?? undefined,
    })
  }

  const handleDownloadCSVClick = () => {
    if (!activeBuilding) return

    exportUnits({
      ...queryParams,
      fileName: buildCsvFilename({
        building: activeBuilding.name,
        context: [filtersState.status],
        date: new Date(),
        page: 'units',
        timezone: activeBuilding.timezone,
      }),
    })
  }

  const fromDateFilter = filters.getFilter('fromDate')
  const toDateFilter = filters.getFilter('toDate')
  const dateRange: DateRange = [
    fromDateFilter ? new Date(fromDateFilter) : null,
    toDateFilter ? new Date(toDateFilter) : null,
  ]

  const rowActions: RowAction<UnitListItem>[] = [
    {
      label: t('units:list.actions.view'),
      onClick: (unit) => {
        navigate({ params: { unitId: unit.id }, path: '/dashboard/units/:unitId/general' })
      },
      primary: true,
    },
  ]

  return (
    // TODO: Remove Dashboard.Container from here. It's added because announcements is outside of /building-dashboard routes. Padding and background color has to be provided to match rest of the pages.
    <Dashboard.Container className="ctp-units__wrapper">
      <Page>
        <PageHeader title={t('units:list.title')} />
        <TableGroup
          id={UNITS_TABLE_ID}
          columns={getUnitsColumns({ t })}
          data={units}
          error={unitsError}
          loading={isUnitsFetching}
          getRowKey={(unit) => unit.id}
          rowActions={rowActions}
          emptyNoFilters={{
            description: t('units:list.empty.description'),
            title: t('units:list.empty.title'),
          }}
          emptyWithFilters={{
            description: t('units:list.emptyFiltered.description'),
            title: t('units:list.emptyFiltered.title'),
          }}
          filters={
            <Select
              options={getLeaseStatusOptions(t)}
              size="small"
              type="multi"
              value={filters.getFilter('leaseStatus') ?? []}
              placeholder={t('units:list.leaseStatusFilter.placeholder')}
              onChange={handleLeaseStatusChange}
              onReset={() => handleLeaseStatusChange([])}
            />
          }
          navigation={{
            actions: (
              <Button
                onlyBox
                size="small"
                use="secondary"
                title={t('units:list.actions.exportCsv')}
                disabled={!activeBuilding || isUnitsFetching}
                loading={isExportPending}
                onClick={handleDownloadCSVClick}
              >
                <Icon icon={DownloadSimpleIcon} />
              </Button>
            ),
            datePicker: (
              <DatePickerInput
                mode="range"
                placeholder={t('units:list.dateRange.placeholder')}
                size="small"
                value={dateRange}
                onRangeChange={handleDateChange}
              />
            ),
            search: (
              <TextInput
                clearable
                icon={MagnifyingGlassIcon}
                placeholder={t('units:list.search.placeholder')}
                size="small"
                value={filters.getFilter('search')}
                onChange={handleSearchChange}
              />
            ),
            statusTabs: (
              <StatusTabs
                options={statusTabsOptions}
                value={filters.getFilter('status') ?? null}
                disabled={isUnitsFetching}
                onChange={handleStatusChange}
              />
            ),
          }}
          pagination={{
            paginationFilters: filters,
            total,
          }}
          tableFilters={filters}
          sortBy={filters.getFilter('sortBy')}
          sortOrder={filters.getFilter('sortOrder')}
          onSort={handleSort}
        />
      </Page>
    </Dashboard.Container>
  )
}
